import React, { Fragment, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useApiGet } from '../../utils/httpClientTs';
import { getFinanceElfLoanDetail } from '../../endpoints/financeElfLoans';
import type {
  ElfLoanDetailLoan,
  FinanceElfLoanDetailResponse,
} from '../../types/financeElfLoans';

const formatMoney = (value: number, currency?: string): string => {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return `${currency || 'USD'} ${new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)}`;
};

const formatDate = (value?: string | null): string => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString();
};

const MOCK_COSIGNERS = [
  { id: 'cos-1', name: 'Morgan Blake', email: 'morgan.blake@example.org' },
  { id: 'cos-2', name: 'Drew Carter', email: 'drew.carter@example.org' },
];

const ElfLoanDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [expandedGroupKeys, setExpandedGroupKeys] = useState<string[]>([]);
  const [cosignerLoan, setCosignerLoan] = useState<ElfLoanDetailLoan | null>(
    null
  );

  const { data, isLoading, isError, refetch } = useApiGet<
    FinanceElfLoanDetailResponse,
    string
  >(
    ['finance-elf-loan-detail', id!],
    getFinanceElfLoanDetail,
    {
      enabled: !!id,
      retry: 1,
    },
    id!
  );

  const paymentValuesDiffer = useMemo(() => {
    if (!data?.loans?.length || data.loans.length < 2) return false;
    const first = data.loans[0];
    return data.loans.slice(1).some((loan) => {
      return (
        loan.auto_payment !== first.auto_payment ||
        (loan.next_payment_date || '') !== (first.next_payment_date || '') ||
        (loan.anniversary_date || '') !== (first.anniversary_date || '')
      );
    });
  }, [data?.loans]);

  const toggleExpanded = (groupKey: string) => {
    setExpandedGroupKeys((prev) =>
      prev.includes(groupKey)
        ? prev.filter((key) => key !== groupKey)
        : [...prev, groupKey]
    );
  };

  return (
    <Grid container spacing={2} px={2}>
      <Grid item xs={12}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/finance/elf-loans')}
          >
            Back to ELF Loans
          </Button>
          <Typography variant="h4" color="primary">
            ELF Loan Detail
          </Typography>
        </Stack>
      </Grid>

      {isError && !data && (
        <Grid item xs={12}>
          <Alert
            severity="error"
            action={<Button onClick={() => refetch()}>Retry</Button>}
          >
            Unable to load ELF loan detail.
          </Alert>
        </Grid>
      )}

      {isLoading && !data && (
        <Grid item xs={12}>
          <Typography>Loading...</Typography>
        </Grid>
      )}

      {data && (
        <>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Loan Info
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Stack spacing={1.25}>
                  <Typography>
                    <strong>Name:</strong> {data.loan_info.borrower_name || '—'}
                  </Typography>
                  <Typography>
                    <strong>Original Loan Amount:</strong>{' '}
                    {formatMoney(
                      data.loan_info.original_amount,
                      data.loan_info.currency
                    )}
                  </Typography>
                  <Typography>
                    <strong>Principal Balance:</strong>{' '}
                    {formatMoney(
                      data.loan_info.principal_balance,
                      data.loan_info.currency
                    )}
                  </Typography>
                  <Typography>
                    <strong>Interest Balance:</strong>{' '}
                    {formatMoney(
                      data.loan_info.interest_balance,
                      data.loan_info.currency
                    )}
                  </Typography>
                  <Typography>
                    <strong>Accrued Balance:</strong>{' '}
                    {formatMoney(
                      data.loan_info.accrued_balance,
                      data.loan_info.currency
                    )}
                  </Typography>
                  <Typography>
                    <strong>Currency:</strong> {data.loan_info.currency || '—'}
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={1}
                >
                  <Typography variant="h6">Payment Info</Typography>
                  {paymentValuesDiffer && (
                    <Chip
                      label="Values differ by loan"
                      size="small"
                      color="warning"
                      variant="outlined"
                    />
                  )}
                </Stack>
                <Divider sx={{ mb: 2 }} />
                <Stack spacing={1.25} mb={2}>
                  <Typography>
                    <strong>Payment Type:</strong>{' '}
                    {data.payment_info.payment_type || '—'}
                  </Typography>
                  <Typography>
                    <strong>Payment Date:</strong>{' '}
                    {formatDate(data.payment_info.payment_date)}
                  </Typography>
                  <Typography>
                    <strong>Next Payment Due:</strong>{' '}
                    {formatDate(data.payment_info.next_payment_due)}
                  </Typography>
                  <Typography>
                    <strong>Next Interest Billing Date:</strong>{' '}
                    {formatDate(data.payment_info.next_interest_billing_date)}
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Button variant="contained" disabled>
                    Make Payment (Coming soon)
                  </Button>
                  <Button variant="outlined" disabled>
                    Add Account on File (Coming soon)
                  </Button>
                </Stack>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" gutterBottom>
                  Loan Actions
                </Typography>
                <Stack spacing={1}>
                  {data.loans.map((loan) => (
                    <Stack
                      key={loan.id}
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography variant="body2">
                        Loan #{loan.loan_number || loan.id}
                      </Typography>
                      <Button
                        size="small"
                        onClick={() => setCosignerLoan(loan)}
                      >
                        Co-signees
                      </Button>
                    </Stack>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Transaction History
                </Typography>
                <Divider sx={{ mb: 2 }} />
                {!data.transaction_history.length ? (
                  <Typography color="text.secondary">
                    No transaction history found.
                  </Typography>
                ) : (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Event</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell align="right">Total Delta</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.transaction_history.map((group) => {
                        const expanded = expandedGroupKeys.includes(
                          group.group_key
                        );
                        return (
                          <Fragment key={group.group_key}>
                            <TableRow
                              hover
                              sx={{ cursor: 'pointer' }}
                              onClick={() => toggleExpanded(group.group_key)}
                            >
                              <TableCell>{group.event_type}</TableCell>
                              <TableCell>
                                {formatDate(group.event_date)}
                              </TableCell>
                              <TableCell align="right">
                                {formatMoney(
                                  group.total_delta,
                                  data.loan_info.currency
                                )}
                              </TableCell>
                            </TableRow>
                            {expanded &&
                              group.items.map((item) => (
                                <TableRow key={item.log_id}>
                                  <TableCell sx={{ pl: 4 }}>
                                    Loan #{item.loan_number || item.loan_id}
                                  </TableCell>
                                  <TableCell>
                                    {formatDate(item.event_date)}
                                  </TableCell>
                                  <TableCell align="right">
                                    {formatMoney(
                                      item.delta,
                                      data.loan_info.currency
                                    )}
                                  </TableCell>
                                </TableRow>
                              ))}
                          </Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Contact History
                </Typography>
                <Divider sx={{ mb: 2 }} />
                {!!data.contact_history.length ? (
                  <Stack spacing={1}>
                    {data.contact_history.map((entry) => (
                      <Box key={entry.id}>
                        <Typography variant="body2" fontWeight={600}>
                          {entry.subject}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {entry.channel} to {entry.recipient} on{' '}
                          {formatDate(entry.sent_at)}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                ) : (
                  <Typography color="text.secondary">
                    No contact history available in pass 1.
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </>
      )}

      <Dialog
        open={!!cosignerLoan}
        onClose={() => setCosignerLoan(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Mocked Co-signer Data (
          {cosignerLoan?.loan_number || cosignerLoan?.id || 'Loan'})
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.25}>
            {MOCK_COSIGNERS.map((c) => (
              <Box key={c.id}>
                <Typography fontWeight={600}>{c.name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {c.email}
                </Typography>
              </Box>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCosignerLoan(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default ElfLoanDetail;
